import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { formatPromoDeadline, usePricing } from '../../contexts/PricingContext';
import { useDocPaging } from '../../contexts/DocPagingContext';
import { getBuildPaymentPlan } from '../../utils/buildPaymentPlan';
import { getAdRamp } from '../../utils/adRamp';
import { useFinancialModel } from '../../hooks/useFinancialModel';
import { useGuaranteedHires } from '../../hooks/useGuaranteedHires';
import { RECRUITER_MONTHLY_FEE } from '../../utils/roiCalculations';
import { BriefPage } from './BriefPage';
import { BriefHeader } from './BriefHeader';
import { BriefPricingPanel } from './BriefPricingPanel';
import { briefCaseStudies, briefLeaderLaunchPhase, briefLeaderPhase, briefLeaderScalePhase, briefPerformanceGuarantee, briefProtection, briefRetentionUpside, briefProtectionNote, briefTimeline } from '../../data/partnershipBrief';
import { launchBuildPhase } from '../../data/launchProposal';
const usd = (value: number) => '$' + value.toLocaleString('en-US');
const label = 'text-[12px] font-bold uppercase tracking-[0.14em] text-[#4a5560]';
const darkLabel = 'text-[12px] font-bold uppercase tracking-[0.14em] text-[#c5cdd3]';
interface Phase {
  window: string;
  phase: string;
  items: string[];
  highlight: boolean;
  hire: string;
  hireNote?: string;
  ads: string;
  adsNote?: string;
}
interface BriefPageTwoProps {
  /** "launch" is the D2D Launch proposal: the leader search is always part of
   *  the plan, and ownership and the build phase cover the channel build. */
  variant?: 'brief' | 'launch';
}
export function BriefPageTwo({
  variant = 'brief'
}: BriefPageTwoProps) {
  const isLaunch = variant === 'launch';
  const paging = useDocPaging();
  // Every figure on this page follows the live pricing settings.
  const {
    adSpend,
    monthlyTotal,
    buildFee,
    splitBuildFee,
    hasLeader: hasLeaderToggle,
    hasImplementationFee,
    hasRetentionBonuses,
    isPromo,
    promoDeadline,
    regularImplementationFee,
    leaderBuildFee,
    leaderMonthlyFee,
    leaderAdSpendLow,
    hasRecruiter,
    guaranteeRevealed,
    moneyBackGuarantee,
    platformFee,
    campaignFee,
    adSpendChunks
  } = usePricing();
  const guaranteedHires = useGuaranteedHires();
  // Custom month-by-month ad spend from the calculator (e.g. $2,500 in
  // Month 1, scaling to $5,000) flows through the model into the timeline.
  const model = useFinancialModel();
  const ramp = getAdRamp(model);
  const perBucket = campaignFee / adSpendChunks + (hasRecruiter ? RECRUITER_MONTHLY_FEE : 0);
  const feeFor = (spend: number) => platformFee + perBucket * Math.max(1, Math.ceil(spend / 2500));
  // Without a ramp every month uses the deck's figures, exactly as before.
  const [m1, m2, m3, m4] = ramp.hasRamp ? ramp.months : [adSpend, adSpend, adSpend, adSpend];
  const [f1, f2, f3, f4] = ramp.hasRamp ? [m1, m2, m3, m4].map(feeFor) : [monthlyTotal, monthlyTotal, monthlyTotal, monthlyTotal];
  // A launch always starts with the leader search.
  const hasLeader = isLaunch || hasLeaderToggle;
  // Same figure as the Partnership page's guarantee card.
  const protection = [...(moneyBackGuarantee ? briefProtection.map(item => ({
    title: item.title,
    body: hasLeader ? item.bodyWithLeader : item.body
  })) : []),
  // Mirrors the deck: only shown once the guarantee card has been flipped
  // to reveal the performance guarantee.
  ...(guaranteeRevealed ? [{
    title: briefPerformanceGuarantee.title,
    body: briefPerformanceGuarantee.body(guaranteedHires, hasLeader)
  }] : []), {
    title: briefRetentionUpside.title,
    body: briefRetentionUpside.body(hasRetentionBonuses)
  }];
  // The split covers the whole implementation fee, leader add-on included.
  const plan = getBuildPaymentPlan(buildFee + (hasLeader ? leaderBuildFee : 0));
  const buildAtSigning = splitBuildFee ? plan.down : buildFee;
  const installment = splitBuildFee ? plan.monthly : 0;
  const dueAtSigning = hasLeader ? buildAtSigning + (splitBuildFee ? 0 : leaderBuildFee) + leaderMonthlyFee : buildAtSigning + f1;
  const signingCovers = hasLeader ? hasImplementationFee ? 'covers implementation and Month 1 of the leader search' : 'covers Month 1 of the leader search' : hasImplementationFee ? 'covers implementation and the first live month' : 'covers the first live month';

  // Months count from signing. After a leader search the rep campaign starts
  // in the month after the search (Month 3 after a two-month search).
  const searchMonths = hasLeader ? model.leaderMonths || 2 : 0;
  const campaignMonth = (n: number) => searchMonths + n;

  // The timeline is always four columns so the page keeps the same layout
  // whatever is toggled. With a leader search: Implementation → Months 1–2
  // (leader) → Month 3 → Month 4+. Each fee is one short figure on one line;
  // anything extra ("then …", "+ bonuses") sits on a small note line below,
  // so nothing wraps in the narrow columns.
  const [buildPhase, ...campaignPhases] = briefTimeline;
  const buildColumn: Phase = {
    ...(isLaunch ? launchBuildPhase : buildPhase),
    highlight: true,
    hire: usd(dueAtSigning),
    hireNote: 'due at signing',
    ads: 'Not yet'
  };
  const leaderWindow = searchMonths > 1 ? `Months 1–${searchMonths}` : 'Month 1';
  const phases: Phase[] = hasLeader ? [buildColumn, {
    ...briefLeaderPhase,
    window: leaderWindow,
    highlight: true,
    // Month 1 of the search is in the amount due at signing.
    hire: `${usd(leaderMonthlyFee)} / mo`,
    hireNote: searchMonths > 1 ? 'Month 1 already paid' : 'Already paid',
    ads: `${usd(leaderAdSpendLow)} / mo`
  }, {
    ...briefLeaderLaunchPhase,
    window: `Month ${campaignMonth(1)}`,
    highlight: true,
    hire: usd(f1),
    ads: usd(m1)
  }, {
    ...briefLeaderScalePhase,
    window: `Month ${campaignMonth(2)}+`,
    highlight: false,
    // A split fee adds to the first two months here; a ramp shows
    // where it starts and the steady rate it reaches.
    hire: usd(f2 + installment),
    hireNote: installment > 0 || f2 !== f4 ? `then ${usd(f4)} + bonuses` : '+ bonuses',
    ads: usd(m2),
    adsNote: m2 !== m4 ? `then ${usd(m4)}` : undefined
  }] : [buildColumn, {
    ...campaignPhases[0],
    highlight: true,
    // Month 1's fee is part of the amount due at signing, so it isn't
    // shown as a second charge.
    hire: 'Already paid',
    ads: usd(m1)
  }, {
    ...campaignPhases[1],
    highlight: false,
    hire: usd(f2 + installment),
    hireNote: f2 === f3 ? '+ bonuses' : `then ${usd(f3 + installment)} + bonuses`,
    ads: usd(m2),
    adsNote: m2 !== m3 ? `then ${usd(m3)}` : undefined
  }, {
    ...campaignPhases[2],
    highlight: false,
    hire: usd(f4),
    hireNote: '+ bonuses',
    ads: usd(m4)
  }];

  // With a ramp, the ad schedule replaces the momentum clause so the
  // paragraph stays about the same length.
  const rampSentence = !ramp.hasRamp ? null : m1 < m4 ? `Ads start at ${usd(m1)} in Month ${campaignMonth(1)} and scale to ${usd(m4)} / month by Month ${campaignMonth(ramp.steadyFrom)}.` : `Ads start at ${usd(m1)} in Month ${campaignMonth(1)} and settle at ${usd(m4)} / month from Month ${campaignMonth(ramp.steadyFrom)}.`;
  // The panel above shows the standard campaign, so the leader search rate is
  // spelled out here.
  const leaderSentence = hasLeader ? `The leader search runs at ${usd(leaderMonthlyFee)} / mo plus ${usd(leaderAdSpendLow)} in ads; the campaign above starts once the leader signs.` : null;
  // Implementation terms, spelled out so every layout states them. The promo
  // deadline is set in bold so it can't be missed.
  const promoSentence = hasImplementationFee && isPromo ? <>
        Implementation is {usd(buildFee + (hasLeader ? leaderBuildFee : 0))}
        {splitBuildFee ? ` (regularly ${usd(regularImplementationFee)}), split ${usd(plan.down)} at signing, then ${usd(plan.monthly)} in Months ${campaignMonth(2)} and ${campaignMonth(3)},` : ` one time (regularly ${usd(regularImplementationFee)})`}{' '}
        <span className="font-bold text-[#6fd6b0]" data-id="element-710" data-mp="2asdqbuno66p1">
          when you sign by {formatPromoDeadline(promoDeadline)}
        </span>
        .{' '}
      </> : null;
  const splitSentence = hasImplementationFee && !isPromo && splitBuildFee ? `The ${usd(plan.total)} implementation fee is split: ${usd(plan.down)} at signing, then ${usd(plan.monthly)} in Months ${campaignMonth(2)} and ${campaignMonth(3)}.` : null;
  const extraSentences = [splitSentence, leaderSentence, rampSentence].filter((s): s is string => Boolean(s));
  const hasExtras = Boolean(promoSentence) || extraSentences.length > 0;
  return <BriefPage data-id="element-711" data-mp="v8e8tpibfkuh">
      <BriefHeader label="Investment and decision" data-id="element-712" data-mp="v6tpkw1gbobx" />

      <div className="mt-6 flex items-start justify-between gap-8" data-id="element-713" data-mp="3uouep8cse1p8">
        <h1 className="brief-display text-[44px] font-extrabold leading-none tracking-[-0.02em]" data-id="element-714" data-mp="2k8np1ncntu4">
          Priced to prove itself.
        </h1>
        <p className="max-w-[280px] text-right text-[15.5px] leading-[1.5] text-[#2c3640]" data-id="element-715" data-mp="7ijvz7xzxwa8">
          What it costs, when you pay, and what happens if we don't deliver.
        </p>
      </div>

      <section className="mt-5 rounded-xl bg-[#121a20] px-6 pt-5 pb-4" data-id="element-716" data-mp="3az0tjd4kuktz">
        <BriefPricingPanel includeLeader={hasLeader} dueAtSigning={dueAtSigning} data-id="element-717" data-mp="3fyvfr3deqm8p" />
        <div className="mt-3 pt-3 border-t border-[#33404a] flex gap-6" data-id="element-718" data-mp="2sa1e5z6c00wc">
          <span className={`${darkLabel} pt-0.5 shrink-0`} data-id="element-719" data-mp="2wav1orfv3dop">How it starts</span>
          <p className="text-[14.5px] leading-[1.5] text-[#d7dde2]" data-id="element-720" data-mp="2wcy1u4aqan75">
            {hasExtras ? <>
                {usd(dueAtSigning)} at signing {signingCovers}.{' '}
                {promoSentence}
                {extraSentences.join(' ')}
                {extraSentences.length > 0 ? ' ' : ''}We start with a 90-day term from
                the day your first ads go live, then it's month to month, and we
                earn it every month.
              </> : <>
                {usd(dueAtSigning)} at signing {signingCovers}. We start with a
                90-day term from the day your first ads go live so the system
                can build momentum. Then it's month to month, and we earn it
                every month.
              </>}
          </p>
        </div>
      </section>

      {/* Full width, one guarantee per row: title on the left, terms on the
          right. Rows stretch to fill the box, so it never reads half-empty
          whether one, two or three protections are on. */}
      <h2 className={`mt-6 ${label}`} data-id="element-721" data-mp="1davsbwtizjeh">Your protection</h2>
      {/* A minimum height (about the three-guarantee height) so the page
          bottom never opens up; fewer guarantees get larger type to fill it.
          It's a minimum, not a fixed height, so longer terms grow the box
          instead of spilling into the timeline below. */}
      <section className="mt-2.5 min-h-[236px] rounded-xl bg-[#e2f3ea] px-6 pt-2 pb-3.5 flex flex-col" data-id="element-722" data-mp="ulp6w21ckdoz">
        <ol className="flex-1 flex flex-col" data-id="element-723" data-mp="pmmf3jado3yy">
          {protection.map((item, index) => <li key={item.title} className={`flex-1 grid grid-cols-[300px_1fr] items-center gap-8 py-2.5 ${index > 0 ? 'border-t border-[#c4dfd1]' : ''}`} data-id="element-724" data-mp="kni4co7t1y6i" data-mpi={index}>
              <div className={`brief-display font-extrabold leading-[1.15] text-[#14684d] ${protection.length === 1 ? 'text-[28px]' : protection.length === 2 ? 'text-[24px]' : 'text-[20px]'}`} data-id="element-725" data-mp="60suptjua4me">
                {item.title}
              </div>
              <p className={`font-medium leading-[1.45] text-[#1f2a33] antialiased [text-rendering:geometricPrecision] ${protection.length === 1 ? 'text-[18px]' : protection.length === 2 ? 'text-[16.5px]' : 'text-[15px]'}`} data-id="element-726" data-mp="3t7d1hc04674g">
                {item.body}
              </p>
            </li>)}
        </ol>
        <p className="mt-3 pt-2.5 border-t border-[#c4dfd1] text-[13.5px] text-[#4a5560]" data-id="element-727" data-mp="fz8q48pwg01p">
          {briefProtectionNote}
        </p>
      </section>

      <h2 className={`mt-6 ${label}`} data-id="element-728" data-mp="kw8tgbvd6op4">Timeline and payment schedule</h2>
      {/* Each column shares the parent's three rows (heading, items, fees),
          so the fee divider sits at the same height in every column however
          the text wraps. */}
      <div className="mt-2.5 grid grid-cols-4 grid-rows-[auto_1fr_auto] gap-x-3.5" data-id="element-729" data-mp="1mso8e0ha7ops">
        {phases.map(phase => <div key={phase.window} style={{
        display: 'grid',
        gridTemplateRows: 'subgrid',
        gridRow: 'span 3'
      }} className={`border-t-[3px] pt-2 ${phase.highlight ? 'border-[#14684d]' : 'border-[#9aa4ad]'}`} data-id="element-730" data-mp="1iqjgp7ae925s">
            <div data-id="element-731" data-mp="13uc16k9xkowu">
              <div className="brief-display text-[16px] font-bold" data-id="element-732" data-mp="13tsgivswbmf4">
                {phase.window}
              </div>
              <div className="text-[12px] font-bold uppercase tracking-[0.1em] text-[#4a5560]" data-id="element-733" data-mp="zts5ll509tct">
                {phase.phase}
              </div>
            </div>
            <ul className="mt-2 space-y-0.5" data-id="element-734" data-mp="18tew3bw75er3">
              {phase.items.map(item => <li key={item} className="flex gap-1.5 text-[13.5px] leading-snug" data-id="element-735" data-mp="2p7ojo3sm6zgv">
                  <span className={phase.highlight ? 'text-[#14684d]' : 'text-[#9aa4ad]'} aria-hidden="true" data-id="element-736" data-mp="2p9rj1efnq4y9">
                    •
                  </span>
                  <span data-id="element-737" data-mp="yqjl89zdgf5q">{item}</span>
                </li>)}
            </ul>
            <dl className="mt-3 pt-1.5 border-t border-[#d5dade] self-start space-y-0.5" data-id="element-738" data-mp="yse6xqigwbav">
              {[{
            term: 'D2D HIRE',
            value: phase.hire,
            note: phase.hireNote
          }, {
            term: 'Ads',
            value: phase.ads,
            note: phase.adsNote
          }].map(fee => <div key={fee.term} data-id="element-739" data-mp="1co85bflm7i9q">
                  <div className="flex justify-between gap-2 text-[13.5px]" data-id="element-740" data-mp="1hovt7sejrizq">
                    <dt className="shrink-0 whitespace-nowrap" data-id="element-741" data-mp="1mg5haokg6e9i">{fee.term}</dt>
                    <dd className="font-bold text-right whitespace-nowrap" data-id="element-742" data-mp="1mi8im4r1vttj">
                      {fee.value}
                    </dd>
                  </div>
                  {/* If any column has a D2D HIRE note, every column reserves
                      that line so the Ads row sits at the same height. */}
                  {(fee.note || fee.term === 'D2D HIRE' && phases.some(p => p.hireNote)) && <div className="text-right text-[12px] leading-tight text-[#4a5560] whitespace-nowrap" aria-hidden={fee.note ? undefined : true} data-id="element-743" data-mp="3fyl5wlrkssmq">
                      {fee.note || '\u00A0'}
                    </div>}
                </div>)}
            </dl>
          </div>)}
      </div>

      {/* Headline is sized to fill the box; the QR column sits behind a
          hairline so the two halves read as one composed unit. */}
      <section className="mt-6 rounded-xl border border-[#d5dade] px-8 py-5 flex items-center gap-8" data-id="element-744" data-mp="3uuapkd9agtoa">
        <div className="flex-1 min-w-0" data-id="element-745" data-mp="3puy1jp6olgk4">
          <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#14684d]" data-id="element-746" data-mp="r7k307nnctny">
            {briefCaseStudies.eyebrow}
          </div>
          <h2 className="brief-display mt-2 text-[30px] font-extrabold leading-[1.12] tracking-[-0.015em]" data-id="element-747" data-mp="mf0on8h4xui3">
            {briefCaseStudies.titleLines.map(line => <span key={line} className="block whitespace-nowrap" data-id="element-748" data-mp="1aalvkfjx2vkm">
                {line}
              </span>)}
          </h2>
          <p className="mt-3 text-[16px] leading-[1.5] text-[#2c3640]" data-id="element-749" data-mp="wi61i48zu7vu">
            {briefCaseStudies.body}
          </p>
        </div>
        <div className="self-stretch w-px bg-[#e3e7ea]" aria-hidden="true" data-id="element-750" data-mp="mmcic20rrg53" />
        <div className="flex flex-col items-center shrink-0" data-id="element-751" data-mp="mofel3sim5i9">
        <a href={briefCaseStudies.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center" data-id="element-752" data-mp="rlyvkbglkwdw">
          {/* Vector QR code, so it prints sharp at any size. */}
          <QRCodeSVG value={briefCaseStudies.url} size={104} level="M" marginSize={0} fgColor="#141a1f" bgColor="#ffffff" title={`QR code: ${briefCaseStudies.url}`} data-id="element-753" data-mp="hoszspx1gh7c" />
          <span className="mt-1.5 text-[13px] text-[#4a5560]" data-id="element-754" data-mp="egojsns3zhy">
            {briefCaseStudies.qrCaption}
          </span>
        </a>
          {/* A real link, so it stays clickable in a PDF saved from the browser. */}
          <a href={briefCaseStudies.url} target="_blank" rel="noopener noreferrer" className="mt-0.5 text-center text-[13px] font-bold text-[#14684d] underline underline-offset-2 whitespace-nowrap" data-id="element-755" data-mp="a4rz3y1yx6xt">
            {briefCaseStudies.linkLabel}
          </a>
        </div>
      </section>

      {/* pt keeps a clear gap above the footer even when the page is full. */}
      <footer className="mt-auto pt-6 flex items-center justify-between text-[13px] text-[#2c3640]" data-id="element-756" data-mp="o38cu8y8zxpg">
        <span data-id="element-757" data-mp="2utpq5mfbfzas">D2D HIRE · {paging.label(2)}</span>
        <span data-id="element-758" data-mp="2uuhb83baeemt">Full terms in your Services Order</span>
      </footer>
    </BriefPage>;
}